import React, { useReducer } from "react";

const AuthContext = React.createContext();

const AuthProvider = (props) => {
  const initialState = {
    id: localStorage.id,
    username: localStorage.username,
    accessToken: localStorage.accessToken,
    refreshToken: localStorage.refreshToken,
    claims: localStorage.claims ? JSON.parse(localStorage.claims) : [],
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case "setToken":
        return {
          ...state,
          id: action.payload.id,
          username: action.payload.username,
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
          claims: action.payload.claims ?? [],
        };
      case "initialize":
        return {
          ...state,
          id: null,
          username: null,
          accessToken: null,
          refreshToken: null,
          claims: [],
        };
      default:
        throw new Error();
    }
  };

  const [authState, dispatch] = useReducer(reducer, initialState);

  const isAuthenticated = () => {
    return (
      authState.accessToken !== null && authState.accessToken !== undefined
    );
  };

  const hasClaim = (claim) => {
    return authState.claims?.includes(claim);
  };

  const accessToken = () => {
    return authState.accessToken;
  };

  const loggedIn = (
    id,
    username,
    accessToken,
    refreshToken,
    claims,
    callback,
  ) => {
    localStorage.id = id;
    localStorage.accessToken = accessToken;
    localStorage.refreshToken = refreshToken;
    localStorage.username = username;
    localStorage.claims = JSON.stringify(claims ?? []);

    dispatch({
      type: "setToken",
      payload: {
        id,
        username,
        accessToken,
        refreshToken,
        claims: claims || [],
      },
    });
    callback();
  };

  const login = (username, password, success, failure) => {
    fetch("api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      body: JSON.stringify({ username: username, password: password }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error("Login failed. Please try again.");
      })
      .then((data) => {
        loggedIn(
          data.id,
          data.username,
          data.accessToken,
          data.refreshToken,
          data.claims,
          success,
        );
      })
      .catch((error) => {
        if (typeof failure === "function") {
          failure(error.message);
        }
      });
  };

  const refresh = (success, failure) => {
    return fetch("api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      body: JSON.stringify({
        accessToken: localStorage.accessToken,
        refreshToken: localStorage.refreshToken,
      }),
    });
  };

  const init = (callback) => {
    delete localStorage.id;
    delete localStorage.accessToken;
    delete localStorage.refreshToken;
    delete localStorage.username;
    delete localStorage.claims;
    delete localStorage.cp_nav_user_card;

    dispatch({ type: "initialize" });

    if (typeof callback === "function") {
      callback();
    }
  };

  const logout = () => {
    init();
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken: authState.accessToken,
        refreshToken: authState.refreshToken,
        username: authState.username,
        claims: authState.claims,
        login: login,
        logout: logout,
        refresh: refresh,
        loggedIn: loggedIn,
        isAuthenticated: isAuthenticated,
        hasClaim: hasClaim,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

const AuthConsumer = AuthContext.Consumer;

export { AuthContext, AuthProvider, AuthConsumer };
